"use client"

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../stores/auth-store';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { BookOpen, Loader2, AlertCircle, Check } from 'lucide-react';

interface RegisterPageProps {
  onNavigateToLogin: () => void;
}

export function RegisterPage({ onNavigateToLogin }: RegisterPageProps) {
  const { t } = useTranslation();
  const { register, isLoading, error, clearError } = useAuthStore();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const passwordsMatch = password === confirmPassword;
  const passwordMinLength = password.length >= 8;
  const isFormValid = name && email && password && confirmPassword && passwordsMatch && passwordMinLength;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;
    clearError();
    await register({ name, email, password });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-background via-background to-muted/30 p-4">
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center text-center">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
            <BookOpen className="h-7 w-7" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight">reformu.lat</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('auth.registerSubtitle', 'Start writing your masterpiece today')}
          </p>
        </div>

        <Card className="border-border/50 shadow-lg">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-xl">
              {t('auth.createAccount', 'Create an account')}
            </CardTitle>
            <CardDescription>
              {t('auth.createAccountDescription', 'Enter your details to get started')}
            </CardDescription>
          </CardHeader>

          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="name">{t('auth.name', 'Full name')}</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="John Doe"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  autoComplete="name"
                  disabled={isLoading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">{t('auth.email', 'Email')}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  disabled={isLoading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">{t('auth.password', 'Password')}</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  disabled={isLoading}
                />
                {password && (
                  <div className="flex items-center gap-1.5 text-xs">
                    {passwordMinLength ? (
                      <Check className="h-3 w-3 text-green-500" />
                    ) : (
                      <span className="h-3 w-3 rounded-full border border-muted-foreground/50" />
                    )}
                    <span className={passwordMinLength ? 'text-green-600' : 'text-muted-foreground'}>
                      {t('auth.minChars', 'At least 8 characters')}
                    </span>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">
                  {t('auth.confirmPassword', 'Confirm password')}
                </Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  disabled={isLoading}
                />
                {confirmPassword && !passwordsMatch && (
                  <p className="text-xs text-destructive">
                    {t('auth.passwordsMismatch', 'Passwords do not match')}
                  </p>
                )}
              </div>
            </CardContent>

            <CardFooter className="flex flex-col gap-4">
              <Button
                type="submit"
                className="w-full"
                disabled={isLoading || !isFormValid}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('auth.creatingAccount', 'Creating account...')}
                  </>
                ) : (
                  t('auth.createAccount', 'Create account')
                )}
              </Button>

              <div className="text-center text-sm">
                <span className="text-muted-foreground">
                  {t('auth.hasAccount', 'Already have an account?')}{' '}
                </span>
                <button
                  type="button"
                  onClick={onNavigateToLogin}
                  className="font-medium text-primary hover:underline"
                >
                  {t('auth.signIn', 'Sign in')}
                </button>
              </div>
            </CardFooter>
          </form>
        </Card>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          Ao criar uma conta você concorda com os{' '}
          <Link to="/termos" className="underline underline-offset-4 hover:text-foreground transition-colors">
            Termos de Uso
          </Link>{' '}
          e a{' '}
          <Link to="/privacidade" className="underline underline-offset-4 hover:text-foreground transition-colors">
            Política de Privacidade
          </Link>
          .
        </p>
      </div>
    </div>
  );
}
